import { FC, RefObject, useState } from 'react';

import RU from 'date-fns/locale/ru';
import { formatDate, TODAY_START_OF_DAY } from 'utils/date';

import multicreationEndAtChangeAllFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_end_at_change_all_form_submit';
import multicreationStartAtChangeAllFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_start_at_change_all_form_submit';
import { DatePicker, DateType } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { ChangeAllDrop } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableHeader/components/ChangeAllDrop';

import { AuctionCreateFieldKey, updateMultiCreationField } from 'store/models/multiCreation';
import { useDispatch } from 'store/utils';

interface ChangeAllDropProps {
    activatorRef: RefObject<HTMLElement>;
    isVisible: boolean;
    closeDrop: () => void;
    fieldKey: AuctionCreateFieldKey.StartAt | AuctionCreateFieldKey.EndAt;
}

const sendAnalyticsOnApply = (
    fieldKey: AuctionCreateFieldKey.StartAt | AuctionCreateFieldKey.EndAt,
    newDate: string
) => {
    if (fieldKey === AuctionCreateFieldKey.StartAt) {
        multicreationStartAtChangeAllFormSubmit({ newDate });
    }
    if (fieldKey === AuctionCreateFieldKey.EndAt) {
        multicreationEndAtChangeAllFormSubmit({ newDate });
    }
};

const TrlKeys = {
    [AuctionCreateFieldKey.StartAt]: {
        mobileTitle: 'MultiCreationAuction.ChangeAllDatesDrop.startAt.mobileTitle',
    },
    [AuctionCreateFieldKey.EndAt]: {
        mobileTitle: 'MultiCreationAuction.ChangeAllDatesDrop.endAt.mobileTitle',
    },
};

export const ChangeAllDatesDrop: FC<ChangeAllDropProps> = ({ activatorRef, isVisible, closeDrop, fieldKey }) => {
    const [datePickerDate, setDatePickerDate] = useState<Date | undefined>();
    const [displayDate, setDisplayDate] = useState<Date | DateType | undefined>(TODAY_START_OF_DAY);

    const dispatch = useDispatch();
    const trls = useTrls();

    const changeDisplayDate = (date: DateType) => {
        setDisplayDate(date instanceof Date ? date : new Date(date));
    };

    const handleOnAfterExit = () => {
        setDisplayDate(TODAY_START_OF_DAY);
    };

    const handleApplyClick = () => {
        if (!datePickerDate) {
            return;
        }

        const newDate = formatDate(datePickerDate);

        dispatch(updateMultiCreationField({ fieldKey, newValue: newDate }));
        sendAnalyticsOnApply(fieldKey, newDate);
        closeDrop();
    };

    return (
        <ChangeAllDrop
            activatorRef={activatorRef}
            closeDrop={closeDrop}
            onApply={handleApplyClick}
            visible={isVisible}
            onAfterExit={handleOnAfterExit}
            withContentPaddings={false}
            bottomSheetTitle={trls(TrlKeys[fieldKey].mobileTitle)}
        >
            <DatePicker
                alignHeader="left"
                enabledCalendars="dd.mm.yyyy"
                headerMonthFormat="mm.yyyy"
                selectedDate={datePickerDate}
                locale={RU}
                onDateSelect={(date) => setDatePickerDate(date as Date)}
                onDisplayDateChange={changeDisplayDate}
                displayDate={displayDate}
                minDate={TODAY_START_OF_DAY}
            />
        </ChangeAllDrop>
    );
};
